import moment from "moment";
import { type Component } from "vue";

import {
  SPRINT_DARTBOARD_KINDS,
  UNASSIGNED_ASSIGNEE_LABEL,
  UNASSIGNED_PSEUDO_ASSIGNEE_KEY,
} from "~/components/visualization/constants";
import { DARTBOARD_KIND_TO_ICON_INFO_MAP } from "~/constants/dartboard";
import { PRIORITY_COLOR_MAP, PRIORITY_OPTIONS, UNPRIORITIZED_PRIORITY_LABEL } from "~/constants/priority";
import {
  AI_PROPERTY_PSEUDO_DUID,
  ATTACHMENTS_PROPERTY_PSEUDO_DUID,
  COMMENTS_PROPERTY_PSEUDO_DUID,
  DARTBOARD_KIND_PROPERTY_PSEUDO_DUID,
  DOCS_PROPERTY_PSEUDO_DUID,
  LINKS_PROPERTY_PSEUDO_DUID,
  RECENT_PROPERTY_PSEUDO_DUID,
  RELATIONSHIPS_PROPERTY_PSEUDO_DUID,
  REMIND_AT_PROPERTY_PSEUDO_DUID,
  SPACE_PROPERTY_PSEUDO_DUID,
  STATUS_KIND_PROPERTY_PSEUDO_DUID,
  SUBSCRIBERS_PROPERTY_PSEUDO_DUID,
  TRASH_PROPERTY_PSEUDO_DUID,
} from "~/constants/property";
import { ORDERED_RELATIONSHIP_CONFIGURATION_WITH_SUBTASKS } from "~/constants/relationship";
import {
  EXPONENTIAL_SIZES,
  FIBONACCI_SIZES,
  FREE_ENTRY_LIMIT,
  FREE_ENTRY_LIMIT_LABEL,
  FREE_ENTRY_SIZES,
  LINEAR_SIZES,
  SIZE_FORMAT_TO_COLOR_MAP,
  TSHIRT_SIZE_TO_NUMBER_SIZE,
  TSHIRT_SIZES_INCLUDING_NULL,
  UNSIZED_SIZE_LABEL,
} from "~/constants/size";
import { DEFAULT_CHIP_COLOR } from "~/constants/style";
import {
  AiFilterIcon,
  AttachmentFieldIcon,
  CheckedIcon,
  CommentIcon,
  CurrentUserIcon,
  DartboardFieldIcon,
  DocIcon,
  DueDateFieldIcon,
  LinkFieldIcon,
  PriorityFieldIcon,
  PriorityIcon,
  RecentIcon,
  RelationshipsIcon,
  ReminderIcon,
  SizeIcon,
  SpaceFieldIcon,
  StartDateFieldIcon,
  StatusFieldIcon,
  SubscribersFieldIcon,
  TrashIcon,
  UnassignedIcon,
  UncheckedIcon,
} from "~/icons";
import { isDefined } from "~/shared/common";
import {
  DartboardKind,
  FilterApplicability,
  FilterType,
  IconKind,
  IconSize,
  PageKind,
  PropertyKind,
  SizeFormat,
  StatusKind,
  ViewKind,
} from "~/shared/enums";
import type {
  FilterDefinition,
  FilterOption,
  Page,
  Property,
  PropertyConfig,
  PropertyConfigFilter,
} from "~/shared/types";
import { BlockedIcon, CanceledIcon, FinishedIcon, StartedIcon, UnstartedIcon } from "~/status-icons";
import type { useAppStore, usePageStore } from "~/stores";
import type { DataStore } from "~/stores/DataStore";
import { getDescendantDuids, getPageDisplayName, getText } from "~/utils/common";
import { getTimeTrackingDuration } from "~/utils/time";
import { getUserDisplayName } from "~/utils/user";

import * as PropertyDisplay from "./propertyDisplay";

// Initialize stores
type AppStore = ReturnType<typeof useAppStore>;
type PageStore = ReturnType<typeof usePageStore>;

let appStore: AppStore;
let dataStore: DataStore;
let pageStore: PageStore;

let Avatar: Component;
let PageIcon: Component;
let StatusIcon: Component;

export const init = (newDataStore: DataStore, avatar: Component, pageIcon: Component, statusIcon: Component) => {
  dataStore = newDataStore;
  appStore = dataStore.$useAppStore();
  pageStore = dataStore.$usePageStore();
  PropertyDisplay.init(appStore);

  Avatar = avatar;
  PageIcon = pageIcon;
  StatusIcon = statusIcon;
};

export const CURRENT_USER_PSEUDO_USER_KEY = "@CurrentUser";
export const CURRENT_USER_PSEUDO_USER_LABEL = "Current user";

const CURRENT_USER_FILTER_OPTION: FilterOption = {
  value: CURRENT_USER_PSEUDO_USER_KEY,
  label: CURRENT_USER_PSEUDO_USER_LABEL,
  colorHex: DEFAULT_CHIP_COLOR,
  icon: CurrentUserIcon,
  iconArgs: { class: "text-primary-base dark:text-primary-base" },
};

const APPLICABILITY_REMAP_LIST = [
  [FilterApplicability.IS, FilterApplicability.INCLUDE],
  [FilterApplicability.IS_NOT, FilterApplicability.DO_NOT_INCLUDE],
  [FilterApplicability.IS_NOT_SET, FilterApplicability.ARE_NOT_SET],
  [FilterApplicability.EXISTS, FilterApplicability.EXIST],
  [FilterApplicability.CONTAINS, FilterApplicability.CONTAIN],
] as [FilterApplicability, FilterApplicability][];
export const APPLICABILITY_REMAP = new Map([
  ...APPLICABILITY_REMAP_LIST,
  ...APPLICABILITY_REMAP_LIST.map(([k, v]) => [v, k] as const),
]);

const ALWAYS_SET_APPLICABILITIES = [FilterApplicability.IS, FilterApplicability.IS_NOT];

const DEFAULT_APPLICABILITIES = [...ALWAYS_SET_APPLICABILITIES, FilterApplicability.IS_NOT_SET];

const NUMBER_APPLICABILITIES = [
  FilterApplicability.EQUAL,
  FilterApplicability.NOT_EQUAL,
  FilterApplicability.LESS_THAN,
  FilterApplicability.GREATER_THAN,
  FilterApplicability.LESS_THAN_OR_EQUAL,
  FilterApplicability.GREATER_THAN_OR_EQUAL,
  FilterApplicability.IS_NOT_SET,
];

const DEFAULT_ARRAY_APPLICABILITIES = [
  FilterApplicability.INCLUDE,
  FilterApplicability.DO_NOT_INCLUDE,
  FilterApplicability.ARE_NOT_SET,
];

const TEXT_APPLICABILITIES = [FilterApplicability.CONTAINS, FilterApplicability.EXISTS];

const EXISTENCE_ONLY_APPLICABILITIES = [FilterApplicability.EXIST];

const DEFAULT_DATE_APPLICABILITIES = [
  ...DEFAULT_APPLICABILITIES,
  FilterApplicability.EXISTS,
  FilterApplicability.IS_BEFORE,
  FilterApplicability.IS_AFTER,
  FilterApplicability.IS_BETWEEN,
];

const ALWAYS_SET_DATE_APPLICABILITIES = DEFAULT_DATE_APPLICABILITIES.filter(
  (e) => e !== FilterApplicability.IS_NOT_SET && e !== FilterApplicability.EXISTS
);

export const NO_VALUE_APPLICABILITIES = new Set([
  FilterApplicability.IS_NOT_SET,
  FilterApplicability.ARE_NOT_SET,
  FilterApplicability.EXISTS,
  FilterApplicability.EXIST,
  FilterApplicability.IS_CHECKED,
  FilterApplicability.IS_UNCHECKED,
]);

export const ONE_VALUE_APPLICABILITIES = new Set([
  FilterApplicability.IS_BEFORE,
  FilterApplicability.IS_AFTER,
  FilterApplicability.CONTAINS,
  FilterApplicability.CONTAIN,
]);

export const TWO_VALUE_APPLICABILITIES = new Set([FilterApplicability.IS_BETWEEN]);

export const FIELDS_ALWAYS_AVAILABLE_SET = new Set([
  PropertyKind.DEFAULT_DARTBOARD,
  PropertyKind.DEFAULT_ASSIGNEES,
  PropertyKind.DEFAULT_STATUS,
]);

export const STATUS_KIND_ICON_MAP = new Map([
  [StatusKind.UNSTARTED, UnstartedIcon],
  [StatusKind.STARTED, StartedIcon],
  [StatusKind.BLOCKED, BlockedIcon],
  [StatusKind.FINISHED, FinishedIcon],
  [StatusKind.CANCELED, CanceledIcon],
]);

const getUserList = (isAutomation?: boolean) =>
  dataStore.getUserList(isAutomation ? { includeInactiveAgents: true, includeSpecialists: true } : undefined);

const dateFilterFunction = (
  value: string | null,
  filterValue: FilterOption["value"],
  applicability: FilterApplicability | null
) => {
  if (!value || !applicability) {
    return false;
  }

  const dates = Array.isArray(filterValue) ? filterValue : [];
  if (dates.length === 0 || typeof dates[0] !== "string") {
    return false;
  }

  switch (applicability) {
    case FilterApplicability.IS:
    case FilterApplicability.IS_NOT: {
      return moment(value).isSame(moment(dates[0]), "day");
    }
    case FilterApplicability.IS_BEFORE: {
      return moment(value).isBefore(moment(dates[0]), "day");
    }
    case FilterApplicability.IS_AFTER: {
      return moment(value).isAfter(moment(dates[0]), "day");
    }
    case FilterApplicability.IS_BETWEEN: {
      return typeof dates[1] === "string" && moment(value).isBetween(moment(dates[0]), moment(dates[1]), "day", "[]");
    }
    default: {
      return false;
    }
  }
};

const numberFilterFunction = (value: number | null, filterValue: number, applicability: FilterApplicability | null) => {
  switch (applicability) {
    case FilterApplicability.EQUAL: {
      return value === filterValue;
    }
    case FilterApplicability.NOT_EQUAL: {
      return value !== filterValue;
    }
    case FilterApplicability.LESS_THAN: {
      return value !== null && value < filterValue;
    }
    case FilterApplicability.GREATER_THAN: {
      return value !== null && value > filterValue;
    }
    case FilterApplicability.LESS_THAN_OR_EQUAL: {
      return value !== null && value <= filterValue;
    }
    case FilterApplicability.GREATER_THAN_OR_EQUAL: {
      return value !== null && value >= filterValue;
    }
    case FilterApplicability.IS_NOT_SET: {
      return value === null;
    }
    default: {
      throw new Error(`Unexpected applicability: ${applicability}`);
    }
  }
};

export const KIND_FILTER: PropertyConfigFilter<PropertyKind.DEFAULT_KIND> = (property) => [
  {
    type: FilterType.VALUE,
    available: appStore.isPropertyShown(property),
    singleton: true,
    applicabilities: ALWAYS_SET_APPLICABILITIES,
    options: dataStore.taskKindList.map((kind) => ({
      value: kind.duid,
      label: kind.title,
      colorHex: DEFAULT_CHIP_COLOR,
      icon: PageIcon,
      iconArgs: { page: kind, size: IconSize.S },
    })),
    test: (kind, filterValue) => kind === filterValue,
  },
];

export const TITLE_FILTER: PropertyConfigFilter<PropertyKind.DEFAULT_TITLE> = () => [
  {
    type: FilterType.INPUT,
    available: true,
    singleton: true,
    applicabilities: [FilterApplicability.CONTAINS],
    options: [],
    test: (title, filterValue) =>
      typeof filterValue === "string" && title.toLowerCase().includes(filterValue.toLowerCase()),
  },
];

export const DESCRIPTION_FILTER: PropertyConfigFilter<PropertyKind.DEFAULT_DESCRIPTION> = () => [
  {
    type: FilterType.INPUT,
    available: true,
    singleton: true,
    applicabilities: TEXT_APPLICABILITIES,
    options: [],
    test: (description, filterValue) =>
      typeof filterValue === "string" && getText(description).trim().toLowerCase().includes(filterValue.toLowerCase()),
  },
];

export const DARTBOARD_FILTER: PropertyConfigFilter<PropertyKind.DEFAULT_DARTBOARD> = () => {
  const pageIsView = appStore.currentPage?.pageKind === PageKind.VIEW;

  return [
    {
      type: FilterType.VALUE,
      available: pageIsView,
      singleton: true,
      applicabilities: ALWAYS_SET_APPLICABILITIES,
      options: dataStore
        .getDartboardList()
        .map((dartboard) => {
          const pageName = getPageDisplayName(dartboard, dataStore.getSpaceByDuid, "full");
          const spaceAbrev = getPageDisplayName(dartboard, dataStore.getSpaceByDuid, "abrev");
          const adtlSearchTerms = [pageName, spaceAbrev];
          return {
            value: dartboard.duid,
            label: pageName,
            colorHex: DEFAULT_CHIP_COLOR,
            adtlSearchTerms,
            icon: PageIcon,
            iconArgs: { page: dartboard as Page, size: IconSize.S },
          };
        })
        .filter(isDefined),
      test: (dartboard, filterValue) => filterValue === dartboard,
    },
  ];
};

export const STATUS_FILTER: PropertyConfigFilter<PropertyKind.DEFAULT_STATUS | PropertyKind.STATUS> = (property) => [
  {
    type: FilterType.VALUE,
    available: true,
    singleton: true,
    applicabilities: ALWAYS_SET_APPLICABILITIES,
    options: dataStore.getStatusList(property).map((status) => ({
      value: status.duid,
      label: status.title,
      colorHex: status.colorHex,
      icon: StatusIcon,
      iconArgs: { duid: status.duid, isContrast: true },
    })),
    test: (status, filterValue) => status === filterValue,
  },
];

export const USER_FILTER: PropertyConfigFilter<
  PropertyKind.DEFAULT_ASSIGNEES | PropertyKind.DEFAULT_REVIEWERS | PropertyKind.USER
> = (property, _, options) => {
  const pageIsDashboard = appStore.currentPage?.pageKind === PageKind.DASHBOARD;

  return [
    {
      type: FilterType.VALUE,
      available: appStore.isPropertyShown(property),
      singleton: !property.adtl.isMultiple,
      applicabilities: property.adtl.isMultiple ? DEFAULT_ARRAY_APPLICABILITIES : DEFAULT_APPLICABILITIES,
      options: [
        ...([PropertyKind.DEFAULT_ASSIGNEES, PropertyKind.DEFAULT_REVIEWERS].includes(property.kind)
          ? [
              {
                value: pageIsDashboard ? UNASSIGNED_PSEUDO_ASSIGNEE_KEY : null,
                label: UNASSIGNED_ASSIGNEE_LABEL,
                colorHex: DEFAULT_CHIP_COLOR,
                icon: UnassignedIcon,
                iconArgs: { class: "text-lt" },
              },
            ]
          : []),
        ...(options?.isAutomation ? [] : [CURRENT_USER_FILTER_OPTION]),
        ...getUserList(options?.isAutomation).map((user) => ({
          value: user.duid,
          label: getUserDisplayName(user),
          adtlSearchTerms: [user.email],
          colorHex: user.colorHex,
          icon: Avatar,
          iconArgs: {
            user,
            isContrast: pageStore.theme === "light",
          },
        })),
      ],
      test: (users, filterValue) => {
        const valueNorm = property.adtl.isMultiple ? users : users.slice(0, 1);
        return filterValue && typeof filterValue === "string"
          ? valueNorm.includes(filterValue)
          : valueNorm.length === 0;
      },
    },
  ];
};

export const SINGLE_USER_FILTER: PropertyConfigFilter<
  PropertyKind.DEFAULT_CREATED_BY | PropertyKind.DEFAULT_UPDATED_BY
> = (property, _, options) => [
  {
    type: FilterType.VALUE,
    available: appStore.isPropertyShown(property),
    singleton: true,
    applicabilities: DEFAULT_APPLICABILITIES,
    options: [
      ...(options?.isAutomation ? [] : [CURRENT_USER_FILTER_OPTION]),
      ...getUserList(options?.isAutomation).map((user) => ({
        value: user.duid,
        label: getUserDisplayName(user),
        adtlSearchTerms: [user.email],
        colorHex: user.colorHex,
        icon: Avatar,
        iconArgs: {
          user,
          isContrast: pageStore.theme === "light",
        },
      })),
    ],
    test: (userDuid, filterValue) =>
      filterValue && typeof filterValue === "string" ? userDuid === filterValue : userDuid === null,
  },
];

export const OPTION_FILTER: PropertyConfigFilter<
  PropertyKind.DEFAULT_TAGS | PropertyKind.MULTISELECT | PropertyKind.SELECT
> = (property) => {
  const isSingleton = property.kind === PropertyKind.SELECT;

  return [
    {
      type: FilterType.VALUE,
      available: appStore.isPropertyShown(property),
      singleton: isSingleton,
      applicabilities: isSingleton ? DEFAULT_APPLICABILITIES : DEFAULT_ARRAY_APPLICABILITIES,
      options: dataStore.getOptionList(property).map((option) => ({
        value: option.duid,
        label: option.title,
        colorHex: option.colorHex,
      })),
      test: (options, filterValue) => {
        const option = typeof filterValue === "string" && dataStore.getOptionByDuid(filterValue);
        const descendantDuids = new Set(option ? getDescendantDuids(dataStore.getOptionList(property), option) : []);
        if (Array.isArray(options)) {
          return filterValue && typeof filterValue === "string"
            ? options.includes(filterValue) || options.some((e) => descendantDuids.has(e))
            : options.length === 0;
        }
        return options === filterValue || (typeof filterValue === "string" && descendantDuids.has(filterValue));
      },
    },
  ];
};

export const PRIORITY_FILTER: PropertyConfigFilter<PropertyKind.DEFAULT_PRIORITY> = (property) => [
  {
    type: FilterType.VALUE,
    available: appStore.isPropertyShown(property),
    singleton: true,
    applicabilities: DEFAULT_APPLICABILITIES,
    options: PRIORITY_OPTIONS.map((priority) => {
      const hasPriority = priority !== null;
      return {
        value: priority,
        label: hasPriority ? priority.toString() : UNPRIORITIZED_PRIORITY_LABEL,
        adtlSearchTerms: hasPriority ? [priority.toString()] : [],
        colorHex: DEFAULT_CHIP_COLOR,
        icon: hasPriority ? PriorityIcon : PriorityFieldIcon,
        iconArgs: {
          class: "icon-sm",
          style: hasPriority ? { color: PRIORITY_COLOR_MAP.get(priority) } : {},
        },
      };
    }),
    test: (priority, filterValue) => priority === filterValue,
  },
];

export const DATES_FILTER: PropertyConfigFilter<PropertyKind.DEFAULT_DATES | PropertyKind.DATES> = (
  property,
  config
) => {
  const alwaysShowForLayouts = config?.alwaysShowForLayouts?.includes(appStore.layoutKind) ?? false;
  const isAvailable = appStore.isPropertyShown(property) || alwaysShowForLayouts;
  const pageIsDashboard = appStore.currentPage?.pageKind === PageKind.DASHBOARD;
  const isRange = property.adtl.isRange || alwaysShowForLayouts;

  return [
    /* START DATE */
    {
      type: FilterType.DATE,
      available: isAvailable && isRange,
      singleton: true,
      applicabilities: DEFAULT_DATE_APPLICABILITIES,
      options: [],
      test: (value, filterValue, _, applicability) => dateFilterFunction(value[0], filterValue, applicability),
      subId: "@start",
      override: {
        title: (e) => `${PropertyDisplay.getPropertyDisplayTitle(e)} start`,
        icon: () => StartDateFieldIcon,
      },
    },
    /* END DATE */
    {
      type: FilterType.DATE,
      available: isAvailable,
      singleton: true,
      applicabilities: DEFAULT_DATE_APPLICABILITIES,
      options: [],
      test: (value, filterValue, _, applicability) => dateFilterFunction(value[1], filterValue, applicability),
      subId: "@end",
      override: {
        title: (e) =>
          pageIsDashboard
            ? "Date"
            : isRange
              ? `${PropertyDisplay.getPropertyDisplayTitle(e)} end`
              : PropertyDisplay.getPropertyDisplayTitle(e),
        icon: () => DueDateFieldIcon,
      },
    },
  ];
};

export const DATE_FILTER: PropertyConfigFilter<PropertyKind.DEFAULT_CREATED_AT | PropertyKind.DEFAULT_UPDATED_AT> = (
  property
) => [
  {
    type: FilterType.DATE,
    available: appStore.isPropertyShown(property),
    singleton: true,
    applicabilities: ALWAYS_SET_DATE_APPLICABILITIES,
    options: [],
    test: (value, filterValue, _, applicability) => dateFilterFunction(value, filterValue, applicability),
  },
];

export const NULLABLE_DATE_FILTER: PropertyConfigFilter<PropertyKind.DEFAULT_COMPLETED_AT> = (property) => [
  {
    type: FilterType.DATE,
    available: appStore.isPropertyShown(property),
    singleton: true,
    applicabilities: DEFAULT_DATE_APPLICABILITIES,
    options: [],
    test: (value, filterValue, _, applicability) => dateFilterFunction(value, filterValue, applicability),
  },
];

export const SIZE_FILTER: PropertyConfigFilter<PropertyKind.DEFAULT_SIZE> = (property) => {
  const colorMap = SIZE_FORMAT_TO_COLOR_MAP.get(property.adtl.format);

  const getSizeOptions = (sizes: number[]) =>
    sizes.map((size) => ({
      value: size,
      label: size?.toString() ?? UNSIZED_SIZE_LABEL,
      adtlSearchTerms: [size?.toString()],
      colorHex: DEFAULT_CHIP_COLOR,
      icon: SizeIcon,
      iconArgs: {
        class: "icon-sm",
        size,
        format: property.adtl.format,
        style: { color: colorMap?.get(size) },
      },
    }));

  const unsizedOption: FilterOption = {
    value: null,
    label: UNSIZED_SIZE_LABEL,
    adtlSearchTerms: [UNSIZED_SIZE_LABEL],
    colorHex: DEFAULT_CHIP_COLOR,
    icon: SizeIcon,
    iconArgs: { class: "icon-sm", format: property.adtl.format },
  };

  let options: FilterOption[];
  switch (property.adtl.format) {
    case SizeFormat.LINEAR: {
      options = [unsizedOption, ...getSizeOptions(LINEAR_SIZES)];
      break;
    }
    case SizeFormat.FIBONACCI: {
      options = [unsizedOption, ...getSizeOptions(FIBONACCI_SIZES)];
      break;
    }
    case SizeFormat.EXPONENTIAL: {
      options = [unsizedOption, ...getSizeOptions(EXPONENTIAL_SIZES)];
      break;
    }
    case SizeFormat.FREE_ENTRY: {
      const numberOptions = getSizeOptions(FREE_ENTRY_SIZES);
      const limitOption: FilterOption = {
        value: FREE_ENTRY_LIMIT_LABEL,
        label: FREE_ENTRY_LIMIT_LABEL,
        adtlSearchTerms: [],
        colorHex: DEFAULT_CHIP_COLOR,
        icon: SizeIcon,
        iconArgs: { class: "icon-sm", size: FREE_ENTRY_LIMIT, format: property.adtl.format },
      };
      options = [unsizedOption, ...numberOptions, limitOption];
      break;
    }
    default: {
      options = TSHIRT_SIZES_INCLUDING_NULL.map((size) => {
        const value = TSHIRT_SIZE_TO_NUMBER_SIZE.get(size) as number;
        return {
          value,
          label: size?.toString() ?? UNSIZED_SIZE_LABEL,
          adtlSearchTerms: [value?.toString()],
          colorHex: DEFAULT_CHIP_COLOR,
          icon: SizeIcon,
          iconArgs: {
            class: "icon-sm",
            format: property.adtl.format,
            ...(value !== null ? { size: value, style: { color: colorMap?.get(value) } } : {}),
          },
        };
      });
    }
  }

  return [
    {
      type: property.adtl.format === SizeFormat.FREE_ENTRY ? FilterType.NUMBER : FilterType.VALUE,
      available: appStore.isPropertyShown(property),
      singleton: true,
      applicabilities: DEFAULT_APPLICABILITIES,
      options,
      test: (size, filterValue) => {
        if (!filterValue) {
          return size === null;
        }
        if (property.adtl.format === SizeFormat.FREE_ENTRY && filterValue === FREE_ENTRY_LIMIT_LABEL) {
          return typeof size === "number" && size > FREE_ENTRY_LIMIT;
        }
        return size === parseInt(filterValue.toString(), 10);
      },
    },
  ];
};

export const TIME_TRACKING_FILTER: PropertyConfigFilter<
  PropertyKind.DEFAULT_TIME_TRACKING | PropertyKind.TIME_TRACKING
> = (property) => [
  {
    type: FilterType.NUMBER,
    available: appStore.isPropertyShown(property),
    singleton: true,
    applicabilities: NUMBER_APPLICABILITIES,
    options: [],
    test: (value, filterValue, _, applicability) => {
      const filterNum = parseInt(filterValue as string, 10);
      const totalSeconds = getTimeTrackingDuration(value).asMinutes();

      return numberFilterFunction(totalSeconds, filterNum, applicability);
    },
  },
];

export const TEXT_FILTER: PropertyConfigFilter<PropertyKind.TEXT> = (property) => [
  {
    type: FilterType.INPUT,
    available: appStore.isPropertyShown(property),
    singleton: true,
    applicabilities: TEXT_APPLICABILITIES,
    options: [],
    test: (value, filterValue) =>
      typeof filterValue === "string" && value.toLowerCase().includes(filterValue.toLowerCase()),
  },
];

export const NUMBER_FILTER: PropertyConfigFilter<PropertyKind.NUMBER> = (property) => [
  {
    type: FilterType.NUMBER,
    available: appStore.isPropertyShown(property),
    singleton: true,
    applicabilities: NUMBER_APPLICABILITIES,
    options: [],
    test: (value, filterValue, _, applicability) => {
      const filterNum = parseInt(filterValue as string, 10);
      return numberFilterFunction(value, filterNum, applicability);
    },
  },
];

export const CHECKBOX_FILTER: PropertyConfigFilter<PropertyKind.CHECKBOX> = (property) => [
  {
    type: FilterType.VALUE,
    available: appStore.isPropertyShown(property),
    singleton: true,
    applicabilities: [FilterApplicability.IS_CHECKED, FilterApplicability.IS_UNCHECKED],
    options: [
      {
        value: true,
        label: "Checked",
        adtlSearchTerms: ["true"],
        icon: CheckedIcon,
        iconArgs: { class: "text-lt" },
        colorHex: DEFAULT_CHIP_COLOR,
      },
      {
        value: false,
        label: "Unchecked",
        adtlSearchTerms: ["false"],
        icon: UncheckedIcon,
        iconArgs: { class: "text-lt" },
        colorHex: DEFAULT_CHIP_COLOR,
      },
    ],
    test: () => true,
  },
];

export const getFilterDefinitionList = (
  propertiesWithConfig: [Property, PropertyConfig][],
  filterOptions?: { isAutomation?: boolean }
): FilterDefinition[] => {
  const options: FilterDefinition[] = propertiesWithConfig
    .filter(([, config]) => config.filter !== null)
    .flatMap(
      ([property, config]) =>
        config.filter?.(property, config, filterOptions)?.map(
          (e) =>
            ({
              propertyDuid: `${property.duid}${e.subId ? `/${e.subId}` : ""}`,
              title: e.override?.title ? e.override.title(property) : PropertyDisplay.getPropertyDisplayTitle(property),
              icon: e.override?.icon ? e.override.icon() : config.icon,
              config: e,
              getValue: (task) => config.getValue(property, task, true),
            }) satisfies FilterDefinition
        ) ?? []
    );

  /* AI */
  options.unshift({
    propertyDuid: AI_PROPERTY_PSEUDO_DUID,
    title: "AI filter",
    icon: AiFilterIcon,
    config: {
      type: FilterType.AI,
      available: pageStore.isOnline,
      singleton: true,
      applicabilities: [],
      options: [],
      test: () => true,
    },
    getValue: () => null,
  } satisfies FilterDefinition);

  /* Dartboard type */
  options.push({
    propertyDuid: DARTBOARD_KIND_PROPERTY_PSEUDO_DUID,
    title: "Dartboard type",
    icon: DartboardFieldIcon,
    config: {
      type: FilterType.VALUE,
      available: appStore.currentPage?.pageKind === PageKind.VIEW,
      singleton: false,
      applicabilities: ALWAYS_SET_APPLICABILITIES,
      options: [
        {
          value: DartboardKind.CUSTOM,
          label: "Non-sprint",
          colorHex: DEFAULT_CHIP_COLOR,
        },
        ...SPRINT_DARTBOARD_KINDS.map((kind) => {
          const iconInfo = DARTBOARD_KIND_TO_ICON_INFO_MAP[kind];
          return {
            value: kind,
            label: kind === DartboardKind.FINISHED ? "Archive" : kind,
            colorHex: iconInfo.colorHex,
            icon: PageIcon,
            iconArgs: {
              page: {
                pageKind: PageKind.DARTBOARD,
                kind,
                iconKind: IconKind.ICON,
                iconNameOrEmoji: iconInfo.iconNameOrEmoji,
                colorHex: iconInfo.colorHex,
              },
              size: IconSize.S,
              useCurrentColor: true,
            },
          };
        }),
      ],
      test: (dartboardKind, filterValue) => dartboardKind === filterValue,
    },
    getValue: (task) => {
      const dartboard = dataStore.getDartboardByDuid(task.dartboardDuid);
      return dartboard?.kind ?? null;
    },
  } satisfies FilterDefinition);

  /* Space */
  options.push({
    propertyDuid: SPACE_PROPERTY_PSEUDO_DUID,
    title: "Space",
    icon: SpaceFieldIcon,
    config: {
      type: FilterType.VALUE,
      available: appStore.currentPage?.pageKind === PageKind.VIEW,
      singleton: false,
      applicabilities: ALWAYS_SET_APPLICABILITIES,
      options: dataStore.spaceList.map((space) => ({
        value: space.duid,
        label: space.title,
        colorHex: DEFAULT_CHIP_COLOR,
        icon: PageIcon,
        iconArgs: { page: space as Page, size: IconSize.S },
      })),
      test: (spaceDuid, filterValue) => spaceDuid === filterValue,
    },
    getValue: (task) => {
      const dartboard = dataStore.getDartboardByDuid(task.dartboardDuid);
      return dartboard?.spaceDuid ?? null;
    },
  } satisfies FilterDefinition);

  /* Status kind */
  options.push({
    propertyDuid: STATUS_KIND_PROPERTY_PSEUDO_DUID,
    title: "Status type",
    icon: StatusFieldIcon,
    config: {
      type: FilterType.VALUE,
      available: true,
      singleton: false,
      applicabilities: ALWAYS_SET_APPLICABILITIES,
      options: Object.values(StatusKind).map((kind) => ({
        value: kind,
        label: kind,
        colorHex: DEFAULT_CHIP_COLOR,
        icon: STATUS_KIND_ICON_MAP.get(kind),
        iconArgs: { class: "text-lt" },
      })),
      test: (statusKind, filterValue) => statusKind === filterValue,
    },
    getValue: (task) => {
      const status = dataStore.getStatusByDuid(task.statusDuid);
      return status?.kind ?? null;
    },
  } satisfies FilterDefinition);

  /* Relationships */
  options.push({
    propertyDuid: RELATIONSHIPS_PROPERTY_PSEUDO_DUID,
    title: "Relationships",
    icon: RelationshipsIcon,
    config: {
      type: FilterType.VALUE,
      available: true,
      singleton: false,
      applicabilities: [FilterApplicability.INCLUDE, FilterApplicability.DO_NOT_INCLUDE, FilterApplicability.EXIST],
      options: Object.values(ORDERED_RELATIONSHIP_CONFIGURATION_WITH_SUBTASKS).map((relationship) => ({
        value: JSON.stringify([dataStore.getRelationshipKindByKind(relationship.kind).duid, relationship.isForward]),
        label: relationship.label,
        colorHex: DEFAULT_CHIP_COLOR,
        icon: relationship.icon,
        iconArgs: { class: "icon-sm text-vlt" },
      })),
      test: (_, filterValue, task) => {
        if (typeof filterValue !== "string") {
          return false;
        }
        const [kindDuid, isForward] = JSON.parse(filterValue);
        return task.relationships.some((e) => e.kindDuid === kindDuid && e.isForward === isForward);
      },
    },
    getValue: () => null,
  } satisfies FilterDefinition);

  if (!filterOptions?.isAutomation) {
    /* Recent */
    options.push({
      propertyDuid: RECENT_PROPERTY_PSEUDO_DUID,
      title: "Recent",
      icon: RecentIcon,
      config: {
        type: FilterType.VALUE,
        available: true,
        singleton: true,
        applicabilities: EXISTENCE_ONLY_APPLICABILITIES,
        options: [],
        test: (_, __, task) => dataStore.$useUserStore().recents.recentTaskStack.includes(task.duid),
      },
      getValue: () => null,
    } satisfies FilterDefinition);
  }

  /* Docs */
  options.push({
    propertyDuid: DOCS_PROPERTY_PSEUDO_DUID,
    title: "Docs",
    icon: DocIcon,
    config: {
      type: FilterType.VALUE,
      available: true,
      singleton: true,
      applicabilities: EXISTENCE_ONLY_APPLICABILITIES,
      options: [],
      test: (_, __, task) => dataStore.getDocDuidsRelatedToTask(task).length !== 0,
    },
    getValue: () => null,
  } satisfies FilterDefinition);

  /* Comments */
  options.push({
    propertyDuid: COMMENTS_PROPERTY_PSEUDO_DUID,
    title: "Comments",
    icon: CommentIcon,
    config: {
      type: FilterType.INPUT,
      available: true,
      singleton: true,
      applicabilities: [FilterApplicability.CONTAIN, FilterApplicability.EXIST],
      options: [],
      test: (_, filterValue, task) => {
        // TODO incremental make sure that comments filters work once the filters are moved to the BE
        const commentTexts = dataStore
          .getLoadedCommentsByTaskDuidOrdered(task.duid)
          .map((comment) => getText(comment.text).trim().toLowerCase());
        if (commentTexts.length === 0) {
          return false;
        }
        return typeof filterValue === "string" && commentTexts.some((e) => e.includes(filterValue.toLowerCase()));
      },
    },
    getValue: () => null,
  } satisfies FilterDefinition);

  /* Links */
  options.push({
    propertyDuid: LINKS_PROPERTY_PSEUDO_DUID,
    title: "Links",
    icon: LinkFieldIcon,
    config: {
      type: FilterType.VALUE,
      available: true,
      singleton: true,
      applicabilities: EXISTENCE_ONLY_APPLICABILITIES,
      options: [],
      test: (_, __, task) => task.links.length !== 0,
    },
    getValue: () => null,
  } satisfies FilterDefinition);

  /* Attachments */
  options.push({
    propertyDuid: ATTACHMENTS_PROPERTY_PSEUDO_DUID,
    title: "Attachments",
    icon: AttachmentFieldIcon,
    config: {
      type: FilterType.VALUE,
      available: true,
      singleton: true,
      applicabilities: EXISTENCE_ONLY_APPLICABILITIES,
      options: [],
      test: (_, __, task) => task.attachmentDuids.length !== 0,
    },
    getValue: (task) => task.attachmentDuids,
  } satisfies FilterDefinition);

  /* Subscribers */
  options.push({
    propertyDuid: SUBSCRIBERS_PROPERTY_PSEUDO_DUID,
    title: "Subscribers",
    icon: SubscribersFieldIcon,
    config: {
      type: FilterType.VALUE,
      available: true,
      singleton: false,
      applicabilities: DEFAULT_ARRAY_APPLICABILITIES,
      options: [
        ...(filterOptions?.isAutomation ? [] : [CURRENT_USER_FILTER_OPTION]),
        ...getUserList(filterOptions?.isAutomation).map((user) => ({
          value: user.duid,
          label: getUserDisplayName(user),
          adtlSearchTerms: [user.email],
          colorHex: user.colorHex,
          icon: Avatar,
          iconArgs: {
            user,
          },
        })),
      ],
      test: (_, filterValue, task) =>
        filterValue && typeof filterValue === "string"
          ? task.subscriberDuids.includes(filterValue)
          : task.subscriberDuids.length === 0,
    },
    getValue: (task) => task.subscriberDuids,
  } satisfies FilterDefinition);

  /* Reminder */
  options.push({
    propertyDuid: REMIND_AT_PROPERTY_PSEUDO_DUID,
    title: "Reminder",
    icon: ReminderIcon,
    config: {
      type: FilterType.DATE,
      available: true,
      singleton: true,
      applicabilities: DEFAULT_DATE_APPLICABILITIES,
      options: [],
      test: (_, filterValue, task, applicability) => dateFilterFunction(task.remindAt, filterValue, applicability),
    },
    getValue: (task) => task.remindAt,
  } satisfies FilterDefinition);

  /* Trash */
  options.push({
    propertyDuid: TRASH_PROPERTY_PSEUDO_DUID,
    title: "Trash",
    icon: TrashIcon,
    config: {
      type: FilterType.VALUE,
      available: appStore.searchModalOpen || appStore.currentPage?.kind === ViewKind.SEARCH,
      singleton: true,
      applicabilities: EXISTENCE_ONLY_APPLICABILITIES,
      options: [],
      test: (_, __, task) => task.inTrash,
    },
    getValue: () => null,
  } satisfies FilterDefinition);

  return options;
};
